import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Edit2, Save, X, TrendingUp, Calendar, MessageSquare, Plus } from 'lucide-react';
import NextClassCard from '../../components/NextClassCard';
import AttendanceMarkingModal from '../../components/AttendanceMarkingModal';
import QRAttendanceModal from '../../components/QRAttendanceModal';
import { resolveMediaUrl } from '../../../utils/helpers';

// Mock student data for this class
const mockClassStudents = [
  {
    userId: 1,
    firstName: 'You',
    lastName: '(Current User)',
    profileImage: 'https://i.pravatar.cc/150?img=1',
    skillLevel: 'advanced',
    attendanceRate: 95.0,
    enrolledDate: '2026-02-01',
    lastAttended: '2026-02-18',
    sessionsAttended: 19,
    totalSessions: 20,
    progressNotes: [
      {
        id: 100,
        note: 'Excellent technique and leadership on court. Great role model for other students.',
        addedBy: 'Sarah Johnson',
        addedAt: '2026-02-10',
        updatedAt: '2026-02-10'
      }
    ]
  },
  {
    userId: 201,
    firstName: 'Alex',
    lastName: 'Martinez',
    profileImage: 'https://i.pravatar.cc/150?img=11',
    skillLevel: 'intermediate',
    attendanceRate: 92.5,
    enrolledDate: '2026-03-01',
    lastAttended: '2026-02-18',
    sessionsAttended: 25,
    totalSessions: 27,
    progressNotes: [
      {
        id: 1,
        note: 'Great improvement in forehand technique. Working on backhand consistency.',
        addedBy: 'Sarah Johnson',
        addedAt: '2026-02-15',
        updatedAt: '2026-02-15'
      },
      {
        id: 2,
        note: 'Showing strong court awareness. Recommend advancing to intermediate drills next month.',
        addedBy: 'Sarah Johnson',
        addedAt: '2026-02-10',
        updatedAt: '2026-02-10'
      }
    ]
  },
  {
    userId: 202,
    firstName: 'Emma',
    lastName: 'Chen',
    profileImage: 'https://i.pravatar.cc/150?img=12',
    skillLevel: 'beginner',
    attendanceRate: 100,
    enrolledDate: '2026-03-01',
    lastAttended: '2026-02-18',
    sessionsAttended: 27,
    totalSessions: 27,
    progressNotes: [
      {
        id: 3,
        note: 'Excellent attendance and dedication. Mastering basic strokes quickly.',
        addedBy: 'Sarah Johnson',
        addedAt: '2026-02-12',
        updatedAt: '2026-02-12'
      }
    ]
  },
  {
    userId: 203,
    firstName: 'James',
    lastName: 'Wilson',
    profileImage: 'https://i.pravatar.cc/150?img=13',
    skillLevel: 'beginner',
    attendanceRate: 88.0,
    enrolledDate: '2026-03-05',
    lastAttended: '2026-02-17',
    sessionsAttended: 22,
    totalSessions: 25,
    progressNotes: []
  }
];

const ClassStudentsTab = ({ classId, classData, userRole }) => {
  const { theme } = useTheme();
  const [students, setStudents] = useState(mockClassStudents);
  const [editingNoteFor, setEditingNoteFor] = useState(null); // userId
  const [newNote, setNewNote] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null); // userId to show all notes
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showQRAttendanceModal, setShowQRAttendanceModal] = useState(false);

  // Mock next class session
  const nextSession = {
    id: 4,
    date: new Date().toISOString().split('T')[0], // Today
    dayOfWeek: new Date().toLocaleDateString('en-US', { weekday: 'long' }),
    time: classData.time,
    duration: classData.duration,
    facility: classData.facility,
    status: 'upcoming',
    attendees: 0
  };

  const handleAddNote = (userId) => {
    if (!newNote.trim()) return;

    const updatedStudents = students.map(student => {
      if (student.userId === userId) {
        return {
          ...student,
          progressNotes: [
            {
              id: Date.now(),
              note: newNote.trim(),
              addedBy: classData.coachName,
              addedAt: new Date().toISOString().split('T')[0],
              updatedAt: new Date().toISOString().split('T')[0]
            },
            ...student.progressNotes
          ]
        };
      }
      return student;
    });

    setStudents(updatedStudents);
    setNewNote('');
    setEditingNoteFor(null);
  };

  const handleSaveAttendance = (sessionId, attendanceData) => {
    console.log('Saving attendance for session:', sessionId, attendanceData);
    // In real app, this would call API to save attendance
    // Update student attendance rates based on new attendance data
    const updatedStudents = students.map(student => {
      const attendance = attendanceData[student.userId];
      if (attendance && attendance.status !== 'unmarked') {
        const wasPresent = ['present', 'late'].includes(attendance.status);
        const newTotal = student.totalSessions + 1;
        const newAttended = student.sessionsAttended + (wasPresent ? 1 : 0);
        return {
          ...student,
          sessionsAttended: newAttended,
          totalSessions: newTotal,
          attendanceRate: parseFloat(((newAttended / newTotal) * 100).toFixed(1)),
          lastAttended: wasPresent ? nextSession.date : student.lastAttended
        };
      }
      return student;
    });
    setStudents(updatedStudents);
    setShowAttendanceModal(false);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return 'green';
      case 'intermediate': return 'yellow';
      case 'advanced': return 'red';
      default: return 'gray';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div>
      {/* Next Class Card */}
      {(userRole === 'coach' || userRole === 'admin' || userRole === 'owner') && (
        <NextClassCard
          session={nextSession}
          classData={classData}
          onMarkAttendance={() => setShowAttendanceModal(true)}
          onQRAttendance={() => setShowQRAttendanceModal(true)}
        />
      )}

      <div className="mb-6">
        <h3 className={`text-xl font-bold ${theme.text}`}>Student Roster</h3>
        <p className={theme.textSecondary}>{students.length} students enrolled</p>
      </div>

      <div className="space-y-4">
        {students.map(student => (
          <div key={student.userId} className={`${theme.card} rounded-xl overflow-hidden border ${theme.border}`}>
            {/* Student Header */}
            <div className="p-6">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <img
                  src={resolveMediaUrl(student.profileImage)}
                  alt={`${student.firstName} ${student.lastName}`}
                  className="w-16 h-16 rounded-full object-cover"
                />

                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className={`text-lg font-semibold ${theme.text}`}>
                        {student.firstName} {student.lastName}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 bg-${getLevelColor(student.skillLevel)}-100 text-${getLevelColor(student.skillLevel)}-700 rounded text-xs font-semibold`}>
                          {student.skillLevel}
                        </span>
                        <span className={`text-sm ${theme.textSecondary}`}>
                          Enrolled {formatDate(student.enrolledDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <div className="flex items-center gap-1">
                        <TrendingUp size={14} className="text-green-600" />
                        <span className="font-semibold">{student.attendanceRate}%</span>
                      </div>
                      <div className={`text-xs ${theme.textSecondary}`}>Attendance</div>
                    </div>
                    <div>
                      <div className="font-semibold">{student.sessionsAttended}/{student.totalSessions}</div>
                      <div className={`text-xs ${theme.textSecondary}`}>Sessions</div>
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{formatDate(student.lastAttended)}</div>
                      <div className={`text-xs ${theme.textSecondary}`}>Last Attended</div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        <MessageSquare size={14} className="text-blue-600" />
                        <span className="font-semibold">{student.progressNotes.length}</span>
                      </div>
                      <div className={`text-xs ${theme.textSecondary}`}>Notes</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Notes Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h5 className={`font-semibold ${theme.text} flex items-center`}>
                    <MessageSquare size={16} className="mr-2" />
                    Progress Notes
                  </h5>
                  {userRole === 'coach' || userRole === 'admin' || userRole === 'owner' ? (
                    <button
                      onClick={() => {
                        setEditingNoteFor(editingNoteFor === student.userId ? null : student.userId);
                        setNewNote('');
                      }}
                      className={`flex items-center gap-x-1 px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-sm`}
                    >
                      {editingNoteFor === student.userId ? (
                        <>
                          <X size={14} />
                          <span>Cancel</span>
                        </>
                      ) : (
                        <>
                          <Plus size={14} />
                          <span>Add Note</span>
                        </>
                      )}
                    </button>
                  ) : null}
                </div>

                {/* Add Note Form */}
                {editingNoteFor === student.userId && (
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add progress notes, observations, or recommendations..."
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
                      rows={3}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={() => {
                          setEditingNoteFor(null);
                          setNewNote('');
                        }}
                        className={`px-3 py-1.5 border ${theme.border} rounded-lg hover:bg-gray-50 text-sm`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleAddNote(student.userId)}
                        disabled={!newNote.trim()}
                        className={`px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-1`}
                      >
                        <Save size={14} />
                        <span>Save Note</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Notes List */}
                {student.progressNotes.length > 0 ? (
                  <div className="space-y-3">
                    {/* Show only latest note by default, or all if expanded */}
                    {(expandedStudent === student.userId ? student.progressNotes : student.progressNotes.slice(0, 1)).map((note, index) => (
                      <div key={note.id} className={`p-3 rounded-lg ${index === 0 ? 'bg-gray-50 dark:bg-gray-800' : 'bg-gray-100 dark:bg-gray-700'}`}>
                        <p className="text-gray-900 dark:text-gray-100">{note.note}</p>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 flex items-center justify-between">
                          <span>{note.addedBy} • {formatDate(note.addedAt)}</span>
                        </div>
                      </div>
                    ))}

                    {/* Show More/Less Button */}
                    {student.progressNotes.length > 1 && (
                      <button
                        onClick={() => setExpandedStudent(expandedStudent === student.userId ? null : student.userId)}
                        className={`text-sm ${theme.link} hover:underline`}
                      >
                        {expandedStudent === student.userId
                          ? 'Show less'
                          : `Show ${student.progressNotes.length - 1} more note${student.progressNotes.length - 1 > 1 ? 's' : ''}`
                        }
                      </button>
                    )}
                  </div>
                ) : (
                  <p className={`text-sm ${theme.textSecondary} italic`}>
                    No progress notes yet. Add notes to track student development.
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {students.length === 0 && (
        <div className={`${theme.card} text-center p-12 rounded-xl`}>
          <Calendar size={48} className="mx-auto mb-3 opacity-50" />
          <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>No students enrolled yet</h5>
          <p className={theme.textSecondary}>
            Students will appear here once they enroll in this class.
          </p>
        </div>
      )}

      {/* Attendance Marking Modal */}
      <AttendanceMarkingModal
        isOpen={showAttendanceModal}
        onClose={() => setShowAttendanceModal(false)}
        session={nextSession}
        students={students}
        onSaveAttendance={handleSaveAttendance}
      />

      {/* QR Code Scanner Modal */}
      <QRAttendanceModal
        isOpen={showQRAttendanceModal}
        onClose={() => setShowQRAttendanceModal(false)}
        session={nextSession}
        classData={classData}
        students={students}
        onUpdateAttendance={handleSaveAttendance}
      />
    </div>
  );
};

export default ClassStudentsTab;
